import { useMarkNotificationAsRead, useNotifications, useUnreadNotificationsCount } from "api/notifications";
import { PaziaBox, PaziaInlineLoader } from "components/atoms";
import { routes } from "consts";
import { useState } from "react";
import { FaArrowRightLong, FaRegBell } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { resolveLink } from "pages/Notifications/NotificationItem";
import { getRelativeTime } from "utils";
import { htmlParser } from "utils/htmlParser";

const RECENT_NOTIFICATIONS_FETCH_LIMIT = 20;
const NOTIFICATIONS_SHOWN = 5;

export function DashboardNotifications() {
  const [showAll, setShowAll] = useState(false);
  const { notifications, isLoading } = useNotifications(0, RECENT_NOTIFICATIONS_FETCH_LIMIT);
  const { unreadCount } = useUnreadNotificationsCount();
  const markAsReadMutation = useMarkNotificationAsRead();

  const visibleNotifications = (showAll ? notifications : notifications.filter((n) => !n.isRead)).slice(
    0,
    NOTIFICATIONS_SHOWN,
  );

  const handleItemClick = async (notificationId: number, isRead: boolean) => {
    if (!isRead) {
      try {
        await markAsReadMutation.mutateAsync(notificationId);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  return (
    <PaziaBox>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100">
          <FaRegBell />
          <span>Powiadomienia{unreadCount > 0 ? ` (${unreadCount} nieprzeczytanych)` : ""}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button className="text-primary-600 dark:text-primary-400 hover:underline" onClick={() => setShowAll(!showAll)}>
            {showAll ? "Tylko nieprzeczytane" : "Pokaż ostatnie"}
          </button>
          <Link className="flex items-center gap-1 text-gray-500 hover:underline dark:text-gray-400" to={routes.NOTIFICATIONS}>
            Zobacz wszystkie <FaArrowRightLong />
          </Link>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-4 text-gray-400">
          <PaziaInlineLoader size="small" />
        </div>
      )}

      {!isLoading && visibleNotifications.length === 0 && (
        <div className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">
          {showAll ? "Brak powiadomień" : "Brak nowych powiadomień 🎉"}
        </div>
      )}

      {!isLoading && visibleNotifications.length > 0 && (
        <ul>
          {visibleNotifications.map((notification) => (
            <li
              key={notification.id}
              className="border-b border-gray-200 last:border-0 dark:border-gray-700/60"
            >
              <Link
                className="flex items-center gap-3 px-1 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/20"
                to={resolveLink(notification)}
                onClick={() => handleItemClick(notification.id, notification.isRead)}
              >
                <span className="shrink-0 whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
                  {getRelativeTime(notification.notificationDate)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">
                  {!notification.isRead && (
                    <span className="bg-primary-500 mr-1.5 inline-block h-1.5 w-1.5 rounded-full" />
                  )}
                  {htmlParser.toHTML(notification.message)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PaziaBox>
  );
}
